<template>
  <div
    v-editable="blok"
    class="HotSpotImage"
  >
    <div ref="containerRef" class="HotSpotImageWrap relative">
      <nuxt-img
        v-if="blok.image && blok.image.filename"
        preset="standard"
        sizes="sm:100vw desk:1260px"
        class="h-full w-full object-cover"
        :class="blok.focalPoint"
        :src="blok.image.filename"
        :alt="blok.image.alt"
      />
      <div class="absolute top-0 left-0 w-full h-full">
        <div
          v-for="(item) in blok.hotSpots"
          :key="item._uid"
          :style="{ left: `${item.x?.value}%`, top: `${item.y?.value}%` }"
          class="HotSpotDot"
          :class="[
            visibleItem === item._uid ? 'z-50' : 'z-10',
            'absolute inline-flex w-32 h-32 -translate-x-half -translate-y-half rounded-full bg-bg-inverse/50 border-2 border-lightest/50 group hover:bg-bg-inverse/90 hover:border-lightest/90 hover:cursor-pointer transition-all duration-300 ease-out',
            item.x?.value < 40 ? 'justify-start' : item.x?.value < 60 ? 'justify-center' : 'justify-end',
            item.y?.value < 50 ? 'items-start' : 'items-end'
          ]"
          @pointerenter="handlePointerEnter(item._uid)"
          @pointerleave="handlePointerLeave(item._uid)"
          @click="handleClick(item._uid)"
        >
          <div class="absolute top-half left-half w-12 h-12 group-hover:w-8 group-hover:h-8 bg-bg-base rounded-full -translate-x-half -translate-y-half transition-all duration-300 ease-out" />
          <div
            class="HotSpotContent transition-opacity duration-300 ease-out max-w-144 outline outline-1 outline-bg-inverse/10 shadow-lg shrink-0 p-12 bg-bg-base"
            :class="[
              visibleItem === item._uid ? 'opacity-100 z-50' : 'opacity-0 pointer-events-none z-10',
              item.x?.value < 40 ? '-translate-x-32 desk:translate-x-0' : item.x?.value < 60 ? 'translate-x-0' : 'translate-x-32 desk:translate-x-0',
              item.y?.value < 40 ? 'translate-y-32' : item.y?.value < 60 ? '-translate-y-32' : '-translate-y-32'
            ]"
            @pointerenter="handlePointerEnter(item._uid)"
            @pointerleave="handlePointerLeave(item._uid)"
          >
            <ProductCard
              v-if="getHotspotProduct(item)"
              :product="getHotspotProduct(item)"
              :enable-quick-buy="false"
              design="hotSpot"
              :placement="100"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import {
  DatasourceObjectPosition,
  ProductSelector, StoryBlokAsset,
  StoryBlokPropBase,
  StoryBlokSlider
} from '~/constants/types/storyblok';
import { ref, onMounted, onUnmounted, withDefaults } from 'vue';
import ProductCard from '~/components/product/ProductCard.vue';
import { ProductModel } from '~/models/product';

const visibleItem = ref(null);
const containerRef = ref<HTMLElement | null>(null);
let hideTimeout = null;

const handlePointerEnter = (id) => {
  // Only show on hover for non-touch devices (mouse and pen)
  // Touch devices will use click instead
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    // Clear any pending hide timeout when entering
    if (hideTimeout) clearTimeout(hideTimeout);
    visibleItem.value = id;
  }
};

const handlePointerLeave = (id) => {
  // Only hide on hover leave for non-touch devices
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    // Delay hiding by 200ms
    hideTimeout = setTimeout(() => {
      // Only hide if still the same id
      if (visibleItem.value === id) {
        visibleItem.value = null;
      }
    }, 200);
  }
};

const handleClick = (id) => {
  // Toggle visibility on click - primarily for touch devices
  if (visibleItem.value === id) {
    visibleItem.value = null;
  } else {
    // Clear any pending hide timeout
    if (hideTimeout) clearTimeout(hideTimeout);
    visibleItem.value = id;
  }
};

// Close popover when clicking outside (for touch devices)
const handleClickOutside = (event) => {
  if (visibleItem.value && containerRef.value && !containerRef.value.contains(event.target)) {
    visibleItem.value = null;
  }
};

if (process.client) {
  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    if (hideTimeout) clearTimeout(hideTimeout);
  });
}

const getHotspotProduct = (hotspot) => {
  if (!hotspot.product || hotspot.product.length === 0) {
    return null;
  }
  const productPartNo = hotspot.product[0].product?.product?.partNo;
  if (!productPartNo) {
    return null;
  }
  const found = props.products.find(prod =>
    prod.partNo === productPartNo || prod.partNumbers?.includes(productPartNo)
  );
  return found || null;
};

type Item = StoryBlokPropBase & {
  x: StoryBlokSlider;
  y: StoryBlokSlider;
  product: ProductSelector[];
}

type Props = StoryBlokPropBase & {
  hotSpots: Item[];
  image: StoryBlokAsset;
  focalPoint: DatasourceObjectPosition;
  products: ProductSelector[];
}

const props = withDefaults(defineProps<{
  blok: Props,
  products?: ProductModel[],
}>(), {
  products: () => [],
});

</script>

<style scoped lang="postcss">

</style>
